<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@app/store/auth.js'
import { useCatalogStore } from '@app/store/catalog.js'
import { subscribePopUpCampaigns, isCampaignActive } from '@core/services/campaigns.js'
import PopUpCampaignDisplay from './PopUpCampaignDisplay.vue'

const route = useRoute()
const authStore = useAuthStore()
const catalogStore = useCatalogStore()

const isOpen = ref(false)
const currentCampaign = ref(null)
const campaigns = ref([])
const campaignsLoaded = ref(false)
const evaluated = ref(false)
let unsubscribe = null

const isEligibleViewer = computed(() => {
  if (!authStore.isAuthResolved || !authStore.isAuthenticated) return false
  if (authStore.isAdmin || authStore.isAgent) return false
  if (!route.path.startsWith('/app')) return false
  if (!catalogStore.isBootstrapped) return false
  return true
})

function seenStorageKey() {
  const uid = authStore.userId || 'anon'
  return `popup_campaigns_seen:${uid}`
}

function getSeen() {
  try {
    const raw = localStorage.getItem(seenStorageKey())
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch {
    return new Set()
  }
}

function markSeen(id) {
  try {
    const set = getSeen()
    set.add(id)
    localStorage.setItem(seenStorageKey(), JSON.stringify([...set]))
  } catch {
    // ignore quota / disabled storage
  }
}

function evaluate() {
  if (evaluated.value) return
  if (isOpen.value || currentCampaign.value) return
  if (!isEligibleViewer.value) return
  if (!campaignsLoaded.value) return

  evaluated.value = true

  const seen = getSeen()
  const candidate = campaigns.value.find(
    (c) => c.mediaUrl && isCampaignActive(c) && !seen.has(c.id)
  )
  if (candidate) {
    currentCampaign.value = candidate
    isOpen.value = true
  }
}

watch(
  isEligibleViewer,
  (eligible) => {
    if (eligible && !unsubscribe) {
      unsubscribe = subscribePopUpCampaigns((list) => {
        campaigns.value = list
        campaignsLoaded.value = true
        evaluate()
      })
    }
    if (!eligible && unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  if (unsubscribe) unsubscribe()
})

function handleClose() {
  if (currentCampaign.value) markSeen(currentCampaign.value.id)
  isOpen.value = false
  setTimeout(() => {
    currentCampaign.value = null
  }, 450)
}
</script>

<template>
  <PopUpCampaignDisplay v-model="isOpen" :campaign="currentCampaign" @close="handleClose" />
</template>
